import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  BusinessReportPrompt,
  BusinessReportType,
  PromptDataType,
  isAuthoringTemplateType,
  isBusinessReportType,
} from "@/modules/snapshots/businessReports.types";
import { getPromptDataType } from "@/modules/snapshots/components/BusinessReportPromptEditModal.utils";

const DATA_TYPE_LABELS: Record<PromptDataType, string> = {
  categorical: "Categorical",
  numeric: "Numerical",
  date: "Date",
  currency: "Currency",
  relevancy: "Relevancy",
};

const PromptMetadataField: React.FC<{ label: string; value: string; emphasis?: boolean }> = ({
  label,
  value,
  emphasis = false,
}) => (
  <div className="min-w-[140px] space-y-1">
    <span className="block text-xs font-medium uppercase tracking-wide text-gray-400">{label}</span>
    <span className={`block text-sm ${emphasis ? "font-semibold text-gray-900" : "font-medium text-gray-600"}`}>
      {value}
    </span>
  </div>
);

interface Props {
  prompt: BusinessReportPrompt;
  /** Absent when the viewer may not change the prompt (read-only display). */
  onEdit?: () => void;
  renderHeader?: () => React.ReactNode;
  templateType?: BusinessReportType;
}

export const BusinessReportPromptDisplay: React.FC<Props> = ({
  prompt,
  onEdit,
  renderHeader,
  templateType = "snapshot_template",
}) => {
  const isBusinessReport = isBusinessReportType(templateType);
  const hasOptions = !isAuthoringTemplateType(templateType);
  const promptDataType = getPromptDataType(prompt);

  return (
    <div>
      {renderHeader && renderHeader()}

      <div className="whitespace-pre-line border-t border-gray-100 bg-white px-7 py-6 pb-2 leading-relaxed text-gray-700">
        {prompt.prompt}
      </div>

      <div className="border-t border-gray-100 bg-white px-7 py-6">
        <div className="flex items-start justify-between gap-6">
          <div className="flex flex-wrap items-start gap-x-8 gap-y-4">
            {!isBusinessReport && (
              <>
                <PromptMetadataField label="Context" value={prompt.full_context ? "Full context" : "Limited context"} />
                <PromptMetadataField label="Analysis" value={prompt.use_image ? "Image analysis" : "Text analysis"} />
                <PromptMetadataField
                  label="Response type"
                  value={promptDataType ? DATA_TYPE_LABELS[promptDataType] : "Free text"}
                  emphasis
                />
              </>
            )}
            {hasOptions && (
              <>
                <PromptMetadataField
                  label="Retrieval"
                  value={prompt.web_search ? "Web search" : prompt.no_retriever ? "Prompt only" : "Document retrieval"}
                />
                <PromptMetadataField
                  label="Answer format"
                  value={prompt.traffic_light ? "Traffic light rating" : "Free-form answer"}
                />
              </>
            )}
          </div>

          {onEdit && (
            <Button size="xs" type="button" variant="default" onClick={onEdit} className="shrink-0">
              Edit
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
